// Globals
import React, { Fragment } from 'react';
import { trimEnd } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

// Components
import { CheckIcon } from '@/icons';
import { Button, Input } from '@/ui';
import { StyledBlockEnd, StyledTruncate } from '../Styled';
import { Form, FormItem, FormList, FormListError } from '@/components';
import { StyledDivider, StyledFormListItem, StyledLinkInputWrapper, StyledWrapper } from './LinkingToolFormStyles';

// Models
import { IInterLink } from '@/models/linkingTool.model';

// Helpers
import { LinkingFormFields, TCreateLinkingTemplateFormValues } from '../../helpers';
import { useInterLinksFormRules } from '../../hooks';

type TLinkingToolForm = {
  onSubmit: (values: TCreateLinkingTemplateFormValues) => void;
  initialValues?: IInterLink;
  siteURL?: string;
  isLoading?: boolean;
};

const LinkingToolForm = ({ onSubmit, initialValues, siteURL, isLoading }: TLinkingToolForm) => {
  const { t } = useTranslation(['common']);
  const rules = useInterLinksFormRules(!initialValues);

  return (
    <Form onFinish={onSubmit} initialValues={initialValues}>
      <StyledLinkInputWrapper>
        <FormItem
          name={LinkingFormFields.PAGE_PATH}
          label={t('page_path_title')}
          rules={rules[LinkingFormFields.PAGE_PATH]}
          extra={
            <StyledWrapper>
              <StyledTruncate $width="25rem">{trimEnd(siteURL, '/')}</StyledTruncate>
              <h3>/</h3>
            </StyledWrapper>
          }
        >
          <Input />
        </FormItem>
      </StyledLinkInputWrapper>
      <FormList rules={rules[LinkingFormFields.LINKS]} name={LinkingFormFields.LINKS}>
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(({ key, name, ...field }) => (
              <Fragment key={key}>
                <StyledFormListItem required={false} label={t('label_link_index', { index: key + 1 })}>
                  <FormItem
                    {...field}
                    name={[name, LinkingFormFields.TITLE]}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={rules[LinkingFormFields.TITLE]}
                  >
                    <Input placeholder={t('label_link_title')} />
                  </FormItem>
                  <FormItem
                    {...field}
                    name={[name, LinkingFormFields.PATH]}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={rules[LinkingFormFields.PATH]}
                    label={t('label_link_path')}
                    noStyle
                  >
                    <Input placeholder={t('label_link_path')} />
                  </FormItem>
                </StyledFormListItem>
                {fields.length > 1 ? (
                  <Button
                    type="primary"
                    danger
                    size="small"
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(name)}
                  />
                ) : null}
                <StyledDivider />
              </Fragment>
            ))}
            <FormItem>
              <Button
                size="small"
                onClick={() => add()}
                style={{ width: '60%', marginTop: '0.5rem' }}
                icon={<PlusOutlined />}
              >
                {t('add_link')}
              </Button>
              <FormListError errors={errors} />
            </FormItem>
          </>
        )}
      </FormList>
      <StyledBlockEnd>
        <Button type="primary" htmlType="submit" icon={!siteURL && <CheckIcon />} loading={isLoading}>
          {t(!initialValues ? 'btn_create_inter_link' : 'btn_save')}
        </Button>
      </StyledBlockEnd>
    </Form>
  );
};

export default LinkingToolForm;
